import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { Spiner } from '../../components';

import {

    getCollectSharedResponderEntry,

    joinCollectViaSharedResponderLink,

} from '../../services/api/collectDataService';

import {
    resolveContributorUiLocale,
    useContributorUiLocaleSync,
} from '../../utils/collectLocaleI18n';
import { i18n } from '../../services';

import { sharedLinkErrorMessage } from '../../utils/sharedLinkErrorMessage';

import './collect-shared-responder.css';



type SharedEntry = {

    collection_name: string;

    target_roles: string[];

    requires_role_selection: boolean;

    default_role: string | null;

    can_join: boolean;

    blocked_code?: string | null;

    effective_locale?: string | null;

};



const CollectSharedResponderPage = () => {

    const { collectionToken } = useParams<{ collectionToken: string }>();

    const { t } = useTranslation();

    const navigate = useNavigate();



    const [loading, setLoading] = useState(true);

    const [error, setError] = useState<string | null>(null);

    const [entry, setEntry] = useState<SharedEntry | null>(null);

    const [selectedRole, setSelectedRole] = useState('');

    const [joining, setJoining] = useState(false);

    const [showRoleModal, setShowRoleModal] = useState(false);



    useContributorUiLocaleSync(collectionToken, entry?.effective_locale);



    const joinWithRole = useCallback(

        async (role: string) => {

            if (!collectionToken) return;

            setJoining(true);

            setError(null);

            try {

                const res = await joinCollectViaSharedResponderLink(collectionToken, role || undefined);

                const token = res.body?.data?.invitation_token;

                const created =

                    (res.status === 201 || res.body?.status_code === 201) && Boolean(token);

                if (created) {

                    navigate(`/r/${token}`, { replace: true });

                    return;

                }

                const code =

                    res.body?.data && typeof res.body.data === 'object' && res.body.data !== null

                        ? String((res.body.data as { code?: string }).code ?? '')

                        : '';

                setError(

                    sharedLinkErrorMessage(t, {

                        code: code || undefined,

                        description: res.body?.description,

                    }),

                );

                setShowRoleModal(true);

            } catch {

                setError(t('navigation.anErrorOccurred'));

                setShowRoleModal(true);

            } finally {

                setJoining(false);

            }

        },

        [collectionToken, navigate, t],

    );



    useEffect(() => {

        if (!collectionToken) {

            setError(t('form.collectWorkspace.sharedLinkInvalid', 'Invalid responder link'));

            setLoading(false);

            return;

        }



        let cancelled = false;

        (async () => {

            try {

                const res = await getCollectSharedResponderEntry(collectionToken);

                if (cancelled) return;

                if (res.status !== 200 || !res.body?.data) {

                    setError(

                        sharedLinkErrorMessage(t, {

                            code: res.status === 404 ? 'invalid_link' : undefined,

                            description: res.body?.description,

                        }),

                    );

                    setLoading(false);

                    return;

                }

                const data = res.body.data as SharedEntry;

                const uiLocale = resolveContributorUiLocale(data.effective_locale);

                if (!i18n.language?.startsWith(uiLocale)) {

                    await i18n.changeLanguage(uiLocale);

                }

                setEntry(data);



                if (!data.can_join) {

                    setError(

                        sharedLinkErrorMessage(t, {

                            code: data.blocked_code,

                        }) || t('form.collectWorkspace.sharedLinkNotOpen', 'This collection is not open yet.'),

                    );

                    setLoading(false);

                    return;

                }



                if (data.requires_role_selection) {

                    setSelectedRole(data.target_roles[0] || '');

                    setShowRoleModal(true);

                    setLoading(false);

                    return;

                }



                await joinWithRole(data.default_role || data.target_roles[0] || '');

                if (!cancelled) setLoading(false);

            } catch {

                if (!cancelled) {

                    setError(t('navigation.anErrorOccurred'));

                    setLoading(false);

                }

            }

        })();



        return () => {

            cancelled = true;

        };

    }, [collectionToken, joinWithRole, t]);



    const confirmRole = async () => {

        const role = selectedRole.trim();

        if (!role) {

            setError(t('form.collectWorkspace.sharedLinkPickRole', 'Please select your role.'));

            return;

        }

        setShowRoleModal(false);

        setLoading(true);

        await joinWithRole(role);

        setLoading(false);

    };



    if (loading || joining) {

        return (

            <div className="collect-shared-responder-page d-flex justify-content-center align-items-center min-vh-100 bg-dark">

                <Spiner className="spinner-border-sm text-light" />

            </div>

        );

    }



    return (

        <div className="collect-shared-responder-page min-vh-100 bg-dark d-flex align-items-center justify-content-center p-3">

            {error && !showRoleModal && (

                <div className="alert alert-danger mb-0" role="alert">

                    {error}

                </div>

            )}



            {showRoleModal && entry && (

                <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}>

                    <div className="modal-dialog modal-dialog-centered">

                        <div className="modal-content bg-dark border-secondary">

                            <div className="modal-header border-secondary">

                                <h5 className="modal-title text-white">

                                    {t('form.collectWorkspace.sharedLinkRoleTitle', 'Confirm your role')}

                                </h5>

                            </div>

                            <div className="modal-body">

                                <p className="text-light mb-3">

                                    {t('form.collectWorkspace.sharedLinkRoleHint', {

                                        name: entry.collection_name,

                                        defaultValue: 'Select the role that best matches you for « {{name}} ».',

                                    })}

                                </p>

                                {error && (

                                    <div className="alert alert-danger py-2" role="alert">

                                        {error}

                                    </div>

                                )}

                                <select

                                    className="form-select collect-shared-role-select text-light py-3"

                                    value={selectedRole}

                                    onChange={(e) => setSelectedRole(e.target.value)}

                                    aria-label={t('form.collectWorkspace.sharedLinkRoleTitle', 'Confirm your role')}

                                >

                                    {entry.target_roles.map((role) => (

                                        <option key={role} value={role}>

                                            {role}

                                        </option>

                                    ))}

                                </select>

                            </div>

                            <div className="modal-footer border-secondary">

                                <button type="button" className="btn btn-primary" onClick={() => void confirmRole()}>

                                    {t('form.collectWorkspace.sharedLinkStartInterview', 'Start interview')}

                                </button>

                            </div>

                        </div>

                    </div>

                </div>

            )}

        </div>

    );

};



export default CollectSharedResponderPage;


